import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { FaPhoneAlt, FaTimes, FaChevronDown } from "react-icons/fa";
import car from '../images/carrentallogo.png'

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenuAndNavigate = (hash) => {
    if (location.pathname !== "/") {
      window.location.href = `/${hash}`;
    } else {
      document.getElementById(hash.replace("#", ""))?.scrollIntoView({ behavior: "smooth" });
    }
    setIsMenuOpen(false);
  };

  const handleOutsideClick = (e) => {
    if (e.target.id === "menu-overlay") {
      setIsMenuOpen(false);
    }
  };

  const navLinks = [
    { name: "Home", href: "/", hash: "#home" },
    { name: "Features", href: "#features", hash: "#features" },
    { name: "About Us", href: "#aboutus", hash: "#aboutus" },
    { name: "Why Us", href: "#whyus", hash: "#whyus" },
    { name: "Contact", href: "#contactus", hash: "#contactus" },
  ];

  return (
    <div>
      {/* Enhanced Navbar with scroll effect */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled 
          ? 'bg-white/95 backdrop-blur-lg shadow-2xl py-2' 
          : 'bg-gradient-to-r from-blue-900 to-blue-300 py-4'
      }`}>
        <div className="container mx-auto flex justify-between items-center px-4 sm:px-6 h-20">
          {/* Logo with enhanced styling */}
          <div className="flex items-center group">
            <div className="relative">
              <img 
                src={car} 
                alt="Nupura Self Drive Cars" 
                className={`w-16 h-16 rounded-full object-cover border-2 transition-all duration-300 ${
                  isScrolled 
                    ? 'border-blue-500 group-hover:border-blue-600 group-hover:scale-110' 
                    : 'border-white group-hover:border-cyan-300 group-hover:scale-110'
                } shadow-lg`} 
              />
              {/* Animated ring around logo */}
              <div className={`absolute -inset-2 rounded-full border-2 opacity-0 group-hover:opacity-100 animate-ping transition-all duration-300 ${
                isScrolled ? 'border-blue-400' : 'border-cyan-300'
              }`}></div>
            </div>
            <div className="ml-3">
              <h1 className={`text-xl font-bold transition-colors duration-300 ${
                isScrolled ? 'text-blue-900' : 'text-white'
              }`}>
                Nupura Self Drive Cars
              </h1>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navLinks.map((link, index) => (
              <a
                key={index}
                href={link.href}
                onClick={(e) => {
                  e.preventDefault();
                  closeMenuAndNavigate(link.hash);
                }}
                className={`relative px-4 py-2 font-semibold transition-all duration-300 group ${
                  isScrolled 
                    ? 'text-gray-700 hover:text-blue-600' 
                    : 'text-white hover:text-cyan-300'
                }`}
              >
                {link.name}
                {/* Hover underline effect */}
                <span className={`absolute bottom-0 left-0 w-0 h-0.5 transition-all duration-300 group-hover:w-full ${
                  isScrolled ? 'bg-blue-600' : 'bg-cyan-300'
                }`}></span>
                
                {/* Hover background effect */}
                <span className={`absolute inset-0 rounded-lg opacity-0 group-hover:opacity-10 transition-opacity duration-300 ${
                  isScrolled ? 'bg-blue-600' : 'bg-cyan-300'
                }`}></span>
              </a>
            ))}
          </nav>

          {/* Phone Number & CTA */}
          <div className="hidden md:flex items-center space-x-4">
            {/* Phone Number */}
            <a 
              href="tel:+919906984964" 
              className={`flex items-center space-x-3 px-4 py-2 rounded-full transition-all duration-300 group ${
                isScrolled
                  ? 'bg-blue-600 text-white hover:bg-blue-700 hover:shadow-lg'
                  : 'bg-white/20 text-white hover:bg-white/30 backdrop-blur-sm hover:shadow-lg'
              }`}
            >
              <div className={`p-2 rounded-full transition-all duration-300 ${
                isScrolled ? 'bg-blue-700' : 'bg-white/20'
              } group-hover:scale-110`}>
                <FaPhoneAlt size={14} />
              </div>
              <div className="text-sm font-semibold">
                <div>+91 96663 17749</div>
                <div className="text-xs opacity-80">Call Now</div>
              </div>
            </a>

            {/* Book Now Button */}
            <button 
              onClick={() => closeMenuAndNavigate("#contactus")}
              className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white px-6 py-2 rounded-full font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
            >
              Book Now
            </button>
          </div>

          {/* Mobile Hamburger */}
          <div className="lg:hidden flex items-center">
            <button 
              onClick={toggleMenu}
              className={`p-2 rounded-lg transition-all duration-300 ${
                isScrolled 
                  ? 'text-blue-900 hover:bg-blue-100' 
                  : 'text-white hover:bg-white/20'
              }`}
            >
              {isMenuOpen ? (
                <FaTimes size={24} />
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Enhanced Mobile Menu */}
      <div 
        id="menu-overlay"
        onClick={handleOutsideClick}
        className={`fixed inset-0 z-40 transition-all duration-500 ${
          isMenuOpen 
            ? 'bg-black/60 backdrop-blur-sm opacity-100' 
            : 'bg-black/0 backdrop-blur-0 opacity-0 pointer-events-none'
        }`}
      >
        <div 
          className={`fixed right-0 top-0 w-80 h-full bg-gradient-to-b from-blue-900 to-purple-900 shadow-2xl transform transition-transform duration-500 ${
            isMenuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          {/* Mobile Menu Header */}
          <div className="p-6 border-b border-white/20">
            <div className="flex items-center space-x-3">
              <img 
                src={car} 
                alt="Nupura Self Drive Cars" 
                className="w-12 h-12 rounded-full object-cover border-2 border-white/30"
              />
              <div>
                <h2 className="text-white font-bold text-lg">Nupura Self Drive Cars</h2>
              </div>
            </div>
          </div>

          {/* Mobile Menu Links */}
          <div className="p-6 space-y-2">
            {navLinks.map((link, index) => (
              <a
                key={index}
                href={link.href}
                onClick={(e) => {
                  e.preventDefault();
                  closeMenuAndNavigate(link.hash);
                }}
                className="flex items-center justify-between p-4 text-white hover:bg-white/10 rounded-xl transition-all duration-300 group"
              >
                <span className="font-semibold">{link.name}</span>
                <FaChevronDown className="w-4 h-4 transform -rotate-90 group-hover:translate-x-1 transition-transform duration-300" />
              </a>
            ))}
          </div>

          {/* Mobile Contact Section */}
          <div className="absolute bottom-0 left-0 right-0 p-6 border-t border-white/20">
            {/* Phone Number */}
            <a 
              href="tel:+919906984964"
              className="flex items-center space-x-3 p-4 bg-white/10 rounded-xl mb-4 transition-all duration-300 hover:bg-white/20 group"
            >
              <div className="p-2 bg-cyan-500 rounded-full group-hover:scale-110 transition-transform duration-300">
                <FaPhoneAlt size={16} className="text-white" />
              </div>
              <div className="text-white">
                <div className="font-semibold">+91 96663 17749</div>
                <div className="text-sm text-blue-200">Tap to Call</div>
              </div>
            </a>

            {/* Book Now Button */}
            <button 
              onClick={() => closeMenuAndNavigate("#contactus")}
              className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white py-4 rounded-xl font-bold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
            >
              Book Your Vehicle
            </button>
          </div>
        </div>
      </div>

      {/* Spacer for fixed navbar */}
      <div className="h-20"></div>
    </div>
  );
};

export default Navbar;